import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap, map } from 'rxjs';
import { DataService } from '../data/data.service';
import { HttpService } from '../http/http.service';
import { UtilitiesService } from '../utilities/utilities.service';
import {
  IAcceptRejectOfferApiPayload,
  IAcceptRejectOfferApiResponse,
  IApiClaimsHistory,
  ICancelBalance,
  ICancelJobResponse,
  ICancelContractApiPayload,
  ICancelContractApiResponse,
  ICancellationReason,
  IContractData,
  IDeductableData,
  IDeductableReq,
  IOffer,
  IOfferAlreadyAcceptedApiPayload,
  IOfferApiResponse,
  IOffersApiPayload,
  IServiceJobsData,
  ITandC,
} from '../../interfaces/IJob';
import { ACCEPT_REPLACE_BUYOUT_CLAIM, CANCEL_CONTRACT_URL, CANCEL_REASON_URL, CHECK_OFFER_ACCEPTED_URL, CHECK_ASSOCIATED_CONTRACTS_API, CUSTOMER_OPTION, GET_CONTRACTS, GET_OFFERS_URL, getApiUrlForParams, OFFER_STATUS_URL } from '../../utils/api.util';
import { ICustomerOption } from '../../interfaces/IServiceRequestsHistory';
import { normalizeContractAddresses } from '../../utils/helper';
import { IReplaceBuyoutClaimResponse } from '../../components/pages/claim-history/claim-history.interface';
import { IApplianceReplaceBuyoutModal } from '../../components/pages/service-request-history/service-request-detail/review-diagnosis-appliance/review-diagnosis-config';
import { HANDYMAN_ITEM_CODE, WHOLESALE_CLUB_ITEM_CODE, SELECTED_CONTRACT } from '../../utils/constants';
import { LiferayService } from '../liferay/liferay.service';
import { LIFERAY_WEB_CONSTANT } from '../../utils/liferay.constant';
import { ICrossSellClientContent } from '../../interfaces/ICrossSellContent';
import { IWebContant } from '../liferay/liferay.interface';
import { IFeatureFlag } from '../../interfaces/IFeatureFlags';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  private ceEligibilityRequestId = 0;
  isCancelBalanceAPIFailed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isCancelPlanAPIFailed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  constructor(
    private data: DataService,
    private http: HttpService,
    private liferay: LiferayService,
    private utilService: UtilitiesService,
  ) { }

  getContractData(contractNumber: string, contractChanged: boolean = true): Observable<IContractData> {
    const url = getApiUrlForParams(`/${GET_CONTRACTS}`, { subStr: this.data.auth0UserId.getValue(), contractNumber });
    return this.http.get<IContractData>(url).pipe(
      tap((response) => {
        // check and set for any eligibility flags for items in the contract data, such as handyman eligibility
        const checkedItemEligibilityContractData = this.setItemEligibility(response);
        normalizeContractAddresses(checkedItemEligibilityContractData);

        this.data.contractData$.next(checkedItemEligibilityContractData);
        sessionStorage.setItem(SELECTED_CONTRACT, contractNumber);
        if (contractChanged) {
          this.data.jobData$.next(null); // if contractData changes, then apis that had data based on that contract data should be cleaned out.
          this.data.jobDataFailed$.next(false);
        }
      })
    );
  }

  getJobsData(contractNumber: string): Observable<IApiClaimsHistory[]> {
    const url = `/api/${this.data.auth0UserId.getValue()}/contracts/${contractNumber}/service/jobs`;
    return this.http
      .get<IApiClaimsHistory[]>(url)
      .pipe(
        map((response) => {
          const jobs = response ?? [];
          const contractData = this.data.contractData$.getValue();
          if (contractData) {
            const isPlanBYOP = this.utilService.isPlanBYOP(contractData.baseContract.coverageGroup);
            const isPlanSurge = this.utilService.isPlanSurge(contractData.baseContract.coverageGroup);
            jobs.forEach(job => {
              job.showCancelRequestButtonUI = this.utilService.getShowCancelRequestButtonUI(job, isPlanBYOP, isPlanSurge);
              job.showServiceAgainButtonUI = this.utilService.getShowServiceAgainButtonUI(job, isPlanBYOP, isPlanSurge);
            });
          }
          return jobs;
        }),
        tap({
          next: (response) => {
            this.data.jobData$.next(response ?? []);
            this.data.jobDataFailed$.next(false);
          },
          error: () => this.data.jobDataFailed$.next(true),
        })
      );
  }

  // Get recent jobs data (last 3 items)
  getRecentJobsData(contractNumber: string): Observable<IApiClaimsHistory[]> {
    const url = `/api/${this.data.auth0UserId.getValue()}/contracts/${contractNumber}/service/jobhistory`;
    return this.http
      .get<IApiClaimsHistory[]>(url)
      .pipe(
        map((response) => {
          const jobs = response ?? [];
          const contractData = this.data.contractData$.getValue();
          if (contractData) {
            const isPlanBYOP = this.utilService.isPlanBYOP(contractData.baseContract.coverageGroup);
            const isPlanSurge = this.utilService.isPlanSurge(contractData.baseContract.coverageGroup);
            jobs.forEach(job => {
              job.showCancelRequestButtonUI = this.utilService.getShowCancelRequestButtonUI(job, isPlanBYOP, isPlanSurge);
              job.showServiceAgainButtonUI = this.utilService.getShowServiceAgainButtonUI(job, isPlanBYOP, isPlanSurge);
            });
          }
          return jobs;
        }),
        tap((response) => this.data.recentJobData$.next(response))
      );
  }


  fetchCancelBalance(
    contractNumber: string,
    date: string
  ): Observable<ICancelBalance> {
    const url = `/api/${this.data.auth0UserId.getValue()}/contracts/${contractNumber}/contract/cancelbalance?cancelDate=${date}`;
    return this.http.get<ICancelBalance>(url).pipe(tap((response) => this.data.cancelBalanceData$.next(response)));
  }

  fetchTandC(contractNumber: string): Observable<ITandC> {
    const url = `/api/${this.data.auth0UserId.getValue()}/contracts/${contractNumber}/decpage/pdf`;
    return this.http
      .get<ITandC>(url)
      .pipe(tap((response) => this.data.tncPdf$.next(response)));
  }

  cancelJob(
    jobNumber: string,
    reason: string,
    hasTechnicianVisited: string,
    dateAccepted: string
  ): Observable<ICancelJobResponse> {
    if (!jobNumber) {
      throw new Error('Job number is required');
    }
    const url = `/api/public/service/jobs/cancel/${jobNumber}`;
    const req = {
      cancelReason: reason && reason.length > 0 ? reason : 'Customer Requested',
      hasTechnicianVisited: hasTechnicianVisited,
      serviceJobAcceptedDate: dateAccepted,
    };
    return this.http.put<ICancelJobResponse>(url, req);
  }
  getServiceJobData(serviceJobId: string): Observable<IServiceJobsData> {
    const url = `/api/public/service/jobs/${serviceJobId}`;
    return this.http.get<IServiceJobsData>(url);
  }

  fetchDeductibleInfo(
    deductibleReqBody: IDeductableReq
  ): Observable<IDeductableData> {
    const url = '/api/public/searchbyserviceorder';
    return this.http.post<IDeductableData>(url, deductibleReqBody);
  }

  fetchCancelReasons(): Observable<ICancellationReason[]> {
    return this.http.get<ICancellationReason[]>(CANCEL_REASON_URL).pipe(tap((response) => this.data.cancellationReasons$.next(response)));
  }

  getOffers(contractNumber: string, reason: string): Observable<IOfferApiResponse> {
    const payload: IOffersApiPayload = {
      contractNumber,
      reason,
    };
    return this.http.post<IOfferApiResponse>(GET_OFFERS_URL, payload);
  }

  checkOfferAccepted(contractId: string): Observable<IOfferApiResponse> {
    const payload: IOfferAlreadyAcceptedApiPayload = {
      contractId,
    };
    return this.http.post<IOfferApiResponse>(CHECK_OFFER_ACCEPTED_URL, payload);
  }

  cancelContract(contractNumber: string, reasonCode: string, date: string, nonAutoRenewal: boolean = false): Observable<ICancelContractApiResponse> {
    const auth0Id = this.data.auth0UserId.getValue();
    const url = getApiUrlForParams(CANCEL_CONTRACT_URL, { auth0Id, contractNumber });
    let requestData: ICancelContractApiPayload = {
      "reasonCode": reasonCode,
      "cancelDate": date,
      "cancelSource": 'MY ACCT',
    }

    if (nonAutoRenewal) {
      requestData['nonRenewal'] = 'Y';
    }
    return this.http.post<ICancelContractApiResponse>(url, requestData);
  }

  sendOfferStatus(contractNumber: string, reason: string, status: 'accept' | 'reject', offer: IOffer): Observable<IAcceptRejectOfferApiResponse> {
    const payload: IAcceptRejectOfferApiPayload = {
      contractNumber,
      reason,
      status,
      offers: [offer],
    };
    return this.http.post<IAcceptRejectOfferApiResponse>(OFFER_STATUS_URL, payload);
  }

  public customerOption(requestPayLoad: ICustomerOption): Observable<ICustomerOption> {
    return this.http.post<ICustomerOption>(CUSTOMER_OPTION, requestPayLoad);
  }

  public acceptReplaceBuyoutClaim(serviceOrderId: string, contractNumber: string, requestPayLoad: IApplianceReplaceBuyoutModal): Observable<IReplaceBuyoutClaimResponse | null> {
    const auth0Id = this.data.auth0UserId.getValue();
    if (!auth0Id) { return of(null); }
    let url = getApiUrlForParams(ACCEPT_REPLACE_BUYOUT_CLAIM, { auth0Id, contractNumber, serviceOrderId });
    return this.http.put<IReplaceBuyoutClaimResponse>(url, requestPayLoad);
  }

  // CROSS SELL CE ELIGIBILITY CHECK
  fetchCrossSellClientContent(): Observable<IWebContant<ICrossSellClientContent>> {
    const { id, type } = LIFERAY_WEB_CONSTANT.cross_sell_clients;
    return this.liferay.getWebContent(type, id);
  }

  checkAssociatedContracts(contractNumber: string, coverageType: string): Observable<boolean> {
    const url = getApiUrlForParams(CHECK_ASSOCIATED_CONTRACTS_API, { contractNumber });
    const urlWithQuery = `${url}?coverageType=${coverageType}`;

    const vivrAccessToken = this.data.vivrAccessToken$.getValue();
    let options = undefined;

    if (this.data.isVivr$.getValue() && vivrAccessToken) {
      let headers: { [key: string]: string } = this.http.getHeaders(vivrAccessToken);
      options = { headers };
    }

    return this.http.get<boolean | { hasAssociatedContracts: boolean }>(urlWithQuery, options).pipe(
      map((response) => {
        if (typeof response === 'boolean') {
          return response;
        }
        return !!response?.hasAssociatedContracts;
      })
    );
  }

  checkCrossSellCEEligibility(contractData: IContractData, crossSellResponse: ICrossSellClientContent): void {
    const eligibilityList = crossSellResponse ?
      (crossSellResponse.crossSellEnrollmentByType?.find((item: { type: string; clientIds: string[] }) => item.type === 'CE')?.clientIds || []) : [];
    const isClientEligible = eligibilityList.includes(contractData.baseContract.clientId);

    this.resetCrossSellCEEligibility();
    if (isClientEligible && contractData.baseContract.contractNumber) {
      const requestId = this.ceEligibilityRequestId;
      this.checkAssociatedContracts(contractData.baseContract.contractNumber, 'CE').subscribe({
        next: (hasAssociated) => {
          if (requestId !== this.ceEligibilityRequestId) return;
          // false means it is eligible to display CE offer, true means the CE plan is already there and not to display the offer
          this.data.crossSellCEEligible$.next(!hasAssociated);
        },
        error: () => {
          if (requestId !== this.ceEligibilityRequestId) return;
          this.data.crossSellCEEligible$.next(false);
        }
      });
    }
  }

  // Invalidates any pending CE eligibility request so a stale response can't mark a different contract eligible.
  resetCrossSellCEEligibility(): void {
    this.ceEligibilityRequestId++;
    this.data.crossSellCEEligible$.next(false);
  }

  checkCrossSellSurgeEligibility(contractData: IContractData, crossSellResponse: ICrossSellClientContent): void {
    const eligibilityList = crossSellResponse ?
      (crossSellResponse.crossSellEnrollmentByType?.find((item: { type: string; clientIds: string[] }) => item.type === 'SURGE')?.clientIds || []) : [];
    this.data.crossSellSurgeEligible$.next(eligibilityList.includes(contractData.baseContract.clientId));
  }

  setItemEligibility(contractData: IContractData): IContractData {
    // set eligibility for handyman and wholesale club items
    contractData.hasHandyman = false;
    contractData.hasWholesaleClub = false;

    contractData.productBaseCoverage.baseItems.forEach(item => {
      if (item.itemCode === HANDYMAN_ITEM_CODE) {
        contractData.hasHandyman = true;
      }
      if (item.itemCode === WHOLESALE_CLUB_ITEM_CODE) {
        contractData.hasWholesaleClub = true;
      }
    });

    return contractData;
  }
}
